// ============================================================================
// OMS Load shift — move same-city backorders off a heavy delivery day onto an
// earlier, lighter one. Lists the orders due on the scheduled day; each one the
// operator picks is converted to dropship and dispatched direct from the
// supplier on the earlier day (one P2P upgrade per order).
// ============================================================================
import { SwapOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ExpressTag from "../components/express_tag";
import UnloadingTag from "../components/unloading_tag";
import styles from "../index.module.scss";
import { omsOrderPath } from "../format";
import {
  LoadShiftBlocker,
  LoadShiftRow,
  PreponeResult,
  loadShiftCandidates,
  preponeBatch,
} from "./load_shift.api";

const ISO = "YYYY-MM-DD";
const dash = <span style={{ color: "#bbb" }}>—</span>;

const BLOCKER_LABEL: Record<LoadShiftBlocker, [string, string]> = {
  vendor_not_ready: ["Vendor not ready", "The vendor's committed availability is after the prepone date"],
  payment_pending: ["Payment pending", "Not paid enough to ship — it can't be converted to dropship"],
  customer_date: ["Customer date", "This delivery date was set by a reschedule-delivery / reattempt escalation"],
  shares_truck: ["Shares truck", "Rides a truck with a customer-dated order"],
};

const PREPONE_COLOR: Record<string, string> = {
  preponed: "green",
  blocked: "orange",
  not_candidate: "default",
  refused: "orange",
  failed: "red",
};

const fmtDate = (v: string | null) =>
  v ? dayjs(v).format("DD MMM (ddd)") : "—";

const label = (v: string) => v.replace(/_/g, " ");

const OmsLoadShift: React.FC = () => {
  const [scheduled, setScheduled] = useState<Dayjs | null>(null);
  const [preponeTo, setPreponeTo] = useState<Dayjs | null>(null);
  const [rows, setRows] = useState<LoadShiftRow[]>([]);
  const [loadedFor, setLoadedFor] = useState<{ scheduled: string; preponeTo: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [city, setCity] = useState<string | undefined>(undefined);
  const [showBlocked, setShowBlocked] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [reason, setReason] = useState("");
  const [preponing, setPreponing] = useState(false);
  const [preponeResults, setPreponeResults] = useState<Record<number, PreponeResult>>({});

  const load = async () => {
    if (!scheduled || !preponeTo) return;
    const s = scheduled.format(ISO);
    const p = preponeTo.format(ISO);
    setLoading(true);
    const res = await loadShiftCandidates(s, p);
    setLoading(false);
    if (!res) return;
    setRows(res.rows);
    setLoadedFor({ scheduled: s, preponeTo: p });
    setSelected([]);
    setPreponeResults({});
  };

  const cities = useMemo(
    () => Array.from(new Set(rows.map((r) => r.city).filter(Boolean))) as string[],
    [rows]
  );

  const visible = rows.filter(
    (r) => (!city || r.city === city) && (showBlocked || r.selectable)
  );
  const movable = visible.filter((r) => r.selectable && !preponeResults[r.allocation_id]);

  const doPrepone = async () => {
    if (!loadedFor || !selected.length) return;
    setPreponing(true);
    const results = await preponeBatch({
      scheduled: loadedFor.scheduled,
      prepone_to: loadedFor.preponeTo,
      allocation_ids: selected,
      reason: reason.trim() || undefined,
    });
    setPreponing(false);
    if (!results) return;
    setPreponeResults((prev) => {
      const next = { ...prev };
      results.forEach((r) => (next[r.allocation_id] = r));
      return next;
    });
    setSelected([]);
    const ok = results.filter((r) => r.outcome === "preponed").length;
    const bad = results.length - ok;
    if (bad) message.warning(`${ok} converted, ${bad} not moved — see the Status column`);
    else message.success(`${ok} shipping direct on ${fmtDate(loadedFor.preponeTo)}`);
  };

  const columns: ColumnsType<LoadShiftRow> = [
    {
      title: "PO Number",
      dataIndex: "po_number",
      width: 150,
      fixed: "left",
      render: (v, row) => (
        <Space size={4} wrap>
          {v ? <strong>{v}</strong> : dash}
          <ExpressTag show={row.is_express} />
          <UnloadingTag show={row.is_unloading} />
        </Space>
      ),
    },
    {
      title: "SO",
      dataIndex: "so_number",
      width: 130,
      render: (v, row) => (
        <Link
          to={omsOrderPath(String(row.sales_order_id), { allocationId: row.allocation_id })}
          target="_blank"
          rel="noopener noreferrer"
        >
          {v || `SO ${row.sales_order_id}`}
        </Link>
      ),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      width: 200,
      render: (v, row) => (
        <div>
          <div>{v || dash}</div>
          {(row.supplier_warehouse || row.supplier_city) && (
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              {[row.supplier_warehouse, row.supplier_city].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Hub city each end resolves to (supplier → customer) — what the same-city check compares">
          <span>Hub city</span>
        </Tooltip>
      ),
      dataIndex: "city",
      width: 190,
      render: (v, row) =>
        v ? (
          <span>
            {label(v)} → {row.customer_hub_city ? label(row.customer_hub_city) : "—"}
          </span>
        ) : (
          dash
        ),
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      width: 180,
      render: (v, row) => (
        <div>
          <div>{v || dash}</div>
          {(row.customer_city || row.customer_pincode) && (
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              {[row.customer_city, row.customer_pincode].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Vendor ready",
      dataIndex: "promised_availability_date",
      width: 120,
      render: fmtDate,
    },
    {
      title: "Delivery",
      dataIndex: "target_delivery_date",
      width: 120,
      render: (v, row) =>
        preponeResults[row.allocation_id]?.outcome === "preponed" ? (
          <span>
            <s style={{ color: "#bbb" }}>{fmtDate(v)}</s> {fmtDate(loadedFor?.preponeTo || null)}
          </span>
        ) : (
          fmtDate(v)
        ),
    },
    { title: "Items", dataIndex: "item_count", width: 70, align: "right" },
    {
      title: "Truck",
      key: "truck",
      width: 150,
      render: (_, row) => (
        <Space size={4} wrap>
          {row.truck_booked && (
            <Tooltip title="A truck is booked for this order — converting to dropship replaces it">
              <Tag color="purple">booked</Tag>
            </Tooltip>
          )}
          {row.travels_with.length > 0 && (
            <Tooltip title={`Same truck: ${row.travels_with.join(", ")}`}>
              <Tag>+{row.travels_with.length} on truck</Tag>
            </Tooltip>
          )}
          {!row.truck_booked && !row.travels_with.length && dash}
        </Space>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 220,
      render: (_, row) => {
        const res = preponeResults[row.allocation_id];
        return (
          <Space size={4} wrap>
            {res && (
              <Tooltip title={res.detail}>
                <Tag color={PREPONE_COLOR[res.outcome]}>
                  {res.outcome === "preponed" ? "dropship" : label(res.outcome)}
                </Tag>
              </Tooltip>
            )}
            {!res &&
              row.blockers.map((b) => (
                <Tooltip key={b} title={BLOCKER_LABEL[b][1]}>
                  <Tag color="orange">{BLOCKER_LABEL[b][0]}</Tag>
                </Tooltip>
              ))}
            {!res && row.selectable && <Tag color="green">movable</Tag>}
          </Space>
        );
      },
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <SwapOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>Load shift</h2>
      </div>

      <Card size="small" title="Ship same-city orders direct on an earlier day" style={{ marginBottom: 16 }}>
        <Space wrap align="end" style={{ marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Scheduled (heavy day)</div>
            <DatePicker value={scheduled} onChange={setScheduled} format="DD MMM YYYY (ddd)" />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Prepone to</div>
            <DatePicker
              value={preponeTo}
              onChange={setPreponeTo}
              format="DD MMM YYYY (ddd)"
              disabledDate={(d: Dayjs) =>
                !!d && (d < dayjs().startOf("day") || d.day() === 0 || (!!scheduled && !d.isBefore(scheduled, "day")))
              }
            />
          </div>
          <Button type="primary" onClick={load} loading={loading} disabled={!scheduled || !preponeTo}>
            Find orders
          </Button>
          {loadedFor && (
            <>
              <Select
                value={city}
                onChange={setCity}
                allowClear
                placeholder="All cities"
                style={{ width: 160 }}
                options={cities.map((c) => ({ value: c, label: label(c) }))}
              />
              <Space size={6}>
                <Switch size="small" checked={showBlocked} onChange={setShowBlocked} />
                <span style={{ fontSize: 13 }}>Show blocked</span>
              </Space>
            </>
          )}
        </Space>

        {loadedFor && (
          <>
            <div style={{ color: "#8c8c8c", fontSize: 13, marginBottom: 8 }}>
              {rows.length} same-city backorder{rows.length === 1 ? "" : "s"} due{" "}
              {fmtDate(loadedFor.scheduled)} · {rows.filter((r) => r.selectable).length} movable to{" "}
              {fmtDate(loadedFor.preponeTo)} (vendor ready by then)
            </div>
            <Table
              rowKey="allocation_id"
              size="small"
              columns={columns}
              dataSource={visible}
              scroll={{ x: "max-content" }}
              pagination={false}
              rowSelection={{
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys as number[]),
                getCheckboxProps: (row) => ({
                  disabled: !row.selectable || !!preponeResults[row.allocation_id],
                }),
              }}
            />
            <Space wrap style={{ marginTop: 12 }}>
              <Button size="small" onClick={() => setSelected(movable.map((r) => r.allocation_id))}>
                Select all movable ({movable.length})
              </Button>
              <Input
                placeholder="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{ width: 280 }}
              />
              <Popconfirm
                title={`Ship ${selected.length} order${selected.length === 1 ? "" : "s"} direct on ${fmtDate(loadedFor.preponeTo)}?`}
                description="Each one is converted to dropship and its Zoho PO re-created as a drop-ship PO."
                onConfirm={doPrepone}
                disabled={!selected.length}
              >
                <Button type="primary" disabled={!selected.length} loading={preponing}>
                  Ship direct ({selected.length})
                </Button>
              </Popconfirm>
            </Space>
          </>
        )}
      </Card>

    </div>
  );
};

export default OmsLoadShift;
